import { useApp } from '@/contexts';

import { createElement, useEffect, useMemo, useState } from 'react';

import { useUpdateApplicationMutation } from '@features/apps/api/appsApi';
import { useGetEnvironmentsQuery } from '@features/environments';
import { useGetWorkspaceExecutionLogsQuery } from '@features/monitoring';
import { useGetQuickActionsQuery } from '@features/tenants/api/tenantsApi';
import { useGetTransformersQuery, useGetTriggerSummaryQuery } from '@features/transformers';
import { ExecutionStatusIcon } from '@features/transformers/components/ExecutionStatusIcon';
import { getExecutionStatus } from '@features/transformers/utils/executionStatus';
import {
  CardMembership as CertIcon,
  Edit as EditIcon,
  Share as EnvironmentsIcon,
  History as ExecutionsIcon,
  Help as HelpIcon,
  Description as RecipesIcon,
  Schedule as SchedulesIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArcElement, Chart as ChartJS, Tooltip as ChartTooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router';

import { PageContainer, PageHeading } from '@/components/layout';
import type { ExecutionLog, QuickAction } from '@/types';
import { trackQuickAction } from '@/utils/analytics';
import { openIntercom, trackIntercomEvent } from '@/utils/intercom';
import { logger } from '@/utils/logger';

ChartJS.register(ArcElement, ChartTooltip, Legend, Title);

/**
 * Workspace dashboard overview page
 * Matches legacy dataProtect dashboard with Quick Actions, Monitoring, and Project Description
 */
export const DashboardPage = () => {
  const { appId, tenantId, app } = useApp();
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState('');

  const { data: transformers, isLoading: recipesLoading } = useGetTransformersQuery(appId!, {
    skip: !appId,
  });

  // Get environments using tenantId from context
  const { data: environments, isLoading: envsLoading } = useGetEnvironmentsQuery(
    { tenantId, appId },
    { skip: !tenantId || !appId }
  );

  // Get execution logs for today's count
  const { data: executionLogs = [], isLoading: executionsLoading } =
    useGetWorkspaceExecutionLogsQuery(
      {
        tenantId,
        appId,
        duration: 'past24Hours',
        state: 'all',
      },
      { skip: !tenantId || !appId }
    );

  // Get schedule summary for active schedules count
  const { data: triggerSummary, isLoading: schedulesLoading } = useGetTriggerSummaryQuery(
    {
      tenantId,
      appId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    { skip: !tenantId || !appId }
  );

  const [updateApplication, { isLoading: isSaving }] = useUpdateApplicationMutation();

  // Calculate executions today (from midnight to now)
  const executionsTodayCount = useMemo(() => {
    if (!executionLogs) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return executionLogs.filter((log) => {
      const logDate = new Date(log.startTime);
      return logDate >= today;
    }).length;
  }, [executionLogs]);

  // Calculate active schedules (not paused)
  const activeSchedulesCount = useMemo(() => {
    if (!triggerSummary || !Array.isArray(triggerSummary)) return 0;
    return triggerSummary.filter((schedule) => !schedule.isSchedulesPaused).length;
  }, [triggerSummary]);

  // Initialize description from app data
  useEffect(() => {
    if (app?.description) {
      setDescription(app.description);
    }
  }, [app?.description]);

  const handleSaveDescription = async () => {
    if (!app || !appId) return;

    try {
      await updateApplication({
        tenantId,
        appId,
        data: { description },
      }).unwrap();
      setEditingDescription(false);
    } catch (error) {
      logger.error('Failed to update description:', error);
    }
  };

  const stats = [
    {
      title: 'Recipes',
      count: transformers?.length ?? 0,
      icon: <RecipesIcon sx={{ fontSize: 40 }} />,
      color: '#6C63FF',
      loading: recipesLoading,
    },
    {
      title: 'Environments',
      count: environments?.length ?? 0,
      icon: <EnvironmentsIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      loading: envsLoading,
    },
    {
      title: 'Executions Today',
      count: executionsTodayCount,
      icon: <ExecutionsIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      loading: executionsLoading,
    },
    {
      title: 'Active Schedules',
      count: activeSchedulesCount,
      icon: <SchedulesIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      loading: schedulesLoading,
    },
  ];

  return (
    <PageContainer>
      <PageHeading title="Dashboard" subtitle={`Overview of ${app.name || 'workspace'}`} />

      {/* Stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: 80,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    {stat.loading ? (
                      <Skeleton width={60} height={40} />
                    ) : (
                      <Typography variant="h4">{stat.count}</Typography>
                    )}
                  </Box>
                  <Box sx={{ color: stat.color, opacity: 0.8 }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions Section */}
      <QuickActions appId={appId!} />

      {/* Monitoring Section */}
      <MonitoringSection appId={appId!} />

      {/* Project Description Section */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
            Project Description
          </Typography>
          {!editingDescription ? (
            <Tooltip title="Edit description">
              <IconButton size="small" onClick={() => setEditingDescription(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  setDescription(app.description || '');
                  setEditingDescription(false);
                }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveDescription}
                disabled={isSaving}
              >
                {isSaving ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
        <CardContent>
          {editingDescription ? (
            <TextField
              fullWidth
              multiline
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description (Markdown supported)..."
              disabled={isSaving}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {description || 'No description.'}
            </Typography>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
};

/**
 * Quick Actions section - with Elevio search in header and action cards
 */
const QuickActions = ({ appId: _appId }: { appId: string }) => {
  const { data: quickActionsData = [] } = useGetQuickActionsQuery();

  const handleActionClick = (action: QuickAction) => {
    trackIntercomEvent(`Quick Action: ${action.name}`);
    trackQuickAction(action.name);

    if (action.actionType === 'elevio') {
      // Open Elevio article
      if (window._elev) {
        const articleId = parseInt(action.actionArg);
        window._elev.on?.('load', () => {
          window._elev?.openArticle?.(articleId);
        });
      }
    } else if (action.actionType === 'external-link') {
      // Open external link
      window.open(
        action.actionArg,
        '_blank',
        'toolbar=yes, location=yes, status=yes, menubar=yes, scrollbars=yes'
      );
    } else if (action.actionType === 'intercom') {
      // Open Intercom live chat
      openIntercom();
    } else if (action.actionType === 'wizard') {
      // Navigate to wizard (TODO: implement wizard navigation)
      // console.log('Navigate to wizard:', action.actionArg);
    } else if (action.actionType === 'template') {
      // Navigate to template (TODO: implement template navigation)
      // console.log('Navigate to template:', action.actionArg);
    }
  };

  // Get icon component based on action name (API returns the real actions)
  const getActionIcon = (action: QuickAction) => {
    // Map action names to icons
    const iconMap: Record<string, React.ReactElement> = {
      Help: <HelpIcon sx={{ fontSize: 45 }} />,
      Certification: <CertIcon sx={{ fontSize: 45 }} />,
      // Add other mappings as needed from API response
    };
    return iconMap[action.name] || <HelpIcon sx={{ fontSize: 45 }} />;
  };

  return (
    <Card sx={{ mb: 3, overflow: 'visible' }} className="elevio-quick-actions">
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          minHeight: 64,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            position: 'absolute',
            left: 16,
            width: 218,
            borderRight: '1px solid',
            borderColor: 'divider',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Quick Actions
        </Typography>
        <Box
          sx={{
            marginLeft: '234px',
            flex: 1,
            position: 'relative',
            overflow: 'visible',
            zIndex: 7,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {createElement('elevio-element', {
            'data-type': 'search',
            id: 'dashboard-elevio-envelope',
          })}
        </Box>
      </Box>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'nowrap', overflowX: 'auto' }}>
          {quickActionsData.map((action: QuickAction) => (
            <Card
              key={`quick-action-${action.name}-${action.actionType}`}
              variant="outlined"
              sx={{
                width: 195,
                minWidth: 195,
                height: 137,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <CardActionArea
                onClick={() => handleActionClick(action)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  p: '20px',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    mb: 1,
                    width: '80%',
                  }}
                >
                  {action.name}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Box sx={{ color: 'primary.main' }}>{getActionIcon(action)}</Box>
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Monitoring section with pie chart showing job success/failure stats
 */
const MonitoringSection = ({ appId }: { appId: string }) => {
  const { tenantId } = useApp();
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('thisWeek');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('any');
  const [statusFilter, setStatusFilter] = useState('All');

  const { data: environments, isLoading: envsLoading } = useGetEnvironmentsQuery(
    { tenantId, appId },
    { skip: !tenantId || !appId }
  );

  // Fetch execution logs from API
  const { data: executionLogs = [], isLoading: logsLoading } = useGetWorkspaceExecutionLogsQuery(
    {
      tenantId,
      appId,
      duration: timeFilter,
      state: 'all',
    },
    { skip: !tenantId || !appId }
  );

  // Process execution logs based on filters
  const { succeeded, failed, inProgress, filteredExecutions } = useMemo(() => {
    const succeeded: ExecutionLog[] = [];
    const failed: ExecutionLog[] = [];
    const inProgress: ExecutionLog[] = [];
    const partialSuccess: ExecutionLog[] = [];
    const all: ExecutionLog[] = [];

    if (!executionLogs) return { succeeded, failed, inProgress, filteredExecutions: [] };

    executionLogs.forEach((log) => {
      // Environment filter - convert both to string for comparison
      if (environmentFilter !== 'all' && log.environmentId) {
        const logEnvId = String(log.environmentId);
        const filterEnvId = String(environmentFilter);
        if (logEnvId !== filterEnvId) {
          return;
        }
      }

      // Type filter
      if (typeFilter !== 'any') {
        if (typeFilter === 'wizard' && !log.executionMetadata?.generatedFromPi) {
          return;
        }
        if (typeFilter !== 'wizard' && log.executionMetadata?.languageVersion !== typeFilter) {
          return;
        }
      }

      // Use shared utility for consistent status classification
      const { status } = getExecutionStatus(log);

      switch (status) {
        case 'running':
          inProgress.push(log);
          break;
        case 'partial':
          partialSuccess.push(log);
          break;
        case 'success':
          succeeded.push(log);
          break;
        case 'error':
          failed.push(log);
          break;
      }

      all.push(log);
    });

    // Status filter for display
    let filtered = all;
    if (statusFilter === 'Successes') filtered = succeeded;
    else if (statusFilter === 'Errors') filtered = failed;
    else if (statusFilter === 'In Progress') filtered = inProgress;
    else if (statusFilter === 'Partial Success') filtered = partialSuccess;

    // Sort by startTime descending (latest first)
    filtered.sort((a, b) => {
      const timeA = new Date(a.startTime).getTime();
      const timeB = new Date(b.startTime).getTime();
      return timeB - timeA; // Descending order
    });

    return { succeeded, failed, inProgress, filteredExecutions: filtered };
  }, [executionLogs, environmentFilter, typeFilter, statusFilter]);

  const chartData = {
    labels: ['Success', 'Error', 'In Progress'],
    datasets: [
      {
        data: [succeeded.length, failed.length, inProgress.length],
        backgroundColor: [
          '#5cb85c', // Success - Green
          '#d9534f', // Error - Red
          '#f0ad4e', // In Progress - Orange
        ],
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const hasData = succeeded.length + failed.length + inProgress.length > 0;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  return (
    <Card sx={{ mb: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
          Monitoring
        </Typography>
      </Box>
      <CardContent>
        <Grid container spacing={3}>
          {/* Left: Pie Chart */}
          <Grid size={{ xs: 12, md: 4 }}>
            {logsLoading ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ height: 200, position: 'relative' }}>
                  {hasData ? (
                    <Doughnut data={chartData} options={chartOptions} />
                  ) : (
                    <Box sx={{ pt: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        No Records
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Jobs: {filteredExecutions.length}</strong>
                </Typography>
                <Typography variant="body2" color="success.main">
                  Successes: {succeeded.length}
                </Typography>
                <Typography variant="body2" color="error.main">
                  Errors: {failed.length}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Right: Filters and Execution List */}
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="monitoring-time-filter-label">Filter by Time</InputLabel>
                  <Select
                    labelId="monitoring-time-filter-label"
                    id="monitoring-time-filter"
                    value={timeFilter}
                    label="Filter by Time"
                    onChange={(e) => setTimeFilter(e.target.value)}
                  >
                    <MenuItem value="pastHour">Past Hour</MenuItem>
                    <MenuItem value="past24Hours">Past 24 Hours</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="pastWeek">Past Week</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small" disabled={envsLoading}>
                  <InputLabel id="monitoring-env-filter-label">All Environments</InputLabel>
                  <Select
                    labelId="monitoring-env-filter-label"
                    id="monitoring-env-filter"
                    value={environmentFilter || 'all'}
                    label="All Environments"
                    onChange={(e) => setEnvironmentFilter(e.target.value as string)}
                  >
                    <MenuItem value="all">All Environments</MenuItem>
                    {environments?.map((env) => (
                      <MenuItem key={env.id} value={env.id}>
                        {env.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="monitoring-type-filter-label">Filter by Type</InputLabel>
                  <Select
                    labelId="monitoring-type-filter-label"
                    id="monitoring-type-filter"
                    value={typeFilter}
                    label="Filter by Type"
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    <MenuItem value="wizard">Wizard</MenuItem>
                    <MenuItem value="2.0">Batch Recipe</MenuItem>
                    <MenuItem value="3.0">Streaming Recipe</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="monitoring-status-filter-label">Filter by Status</InputLabel>
                  <Select
                    labelId="monitoring-status-filter-label"
                    id="monitoring-status-filter"
                    value={statusFilter}
                    label="Filter by Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Successes">Successes</MenuItem>
                    <MenuItem value="Errors">Errors</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Partial Success">Partial Success</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Execution List */}
            {filteredExecutions.length > 0 ? (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {filteredExecutions.map((exec: ExecutionLog) => {
                  const env = environments?.find((e) => e.environmentId === exec.environmentId);

                  const handleClick = () => {
                    // Navigate to execution details - matches dataProtect behavior
                    if (exec.executionMetadata?.generatedFromPi === 'wiz') {
                      // Wizard execution
                      navigate(`/a/${appId}/wiz/health`, {
                        state: {
                          recipeId: exec.executionMetadata.baseId,
                          guid: exec.executionGUID,
                          filter: timeFilter,
                        },
                      });
                    } else {
                      // Regular execution - include env parameter for consistency
                      navigate(
                        `/a/${appId}/health?executionGuid=${exec.externalExecutionId}&env=${exec.environmentId}`
                      );
                    }
                  };

                  return (
                    <Box
                      key={exec.executionGUID || exec.externalExecutionId}
                      onClick={handleClick}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:last-child': {
                          borderBottom: 0,
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
                        <Tooltip title={env?.name || 'Unknown Environment'}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '4px',
                              backgroundColor: env?.color || '#404040',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                        <ExecutionStatusIcon execution={exec} size={18} />
                        <Typography variant="body2">
                          {exec.executionMetadata?.recipeName || 'Unknown Recipe'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {new Date(exec.startTime).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true,
                        })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="info">
                {logsLoading
                  ? 'Loading executions...'
                  : 'No executions found for the selected filters.'}
              </Alert>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
